import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Asteroids.css';

const Asteroids = () => {
  const [ship, setShip] = useState({ x: 200, y: 250, angle: 0, vx: 0, vy: 0 });
  const [bullets, setBullets] = useState([]);
  const [asteroids, setAsteroids] = useState([]);
  const [score, setScore] = useState(0);
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [keys, setKeys] = useState({});
  const [canShoot, setCanShoot] = useState(true);
  const animationRef = useRef(null);

  const GAME_SIZE = 500;

  const createAsteroid = (x, y, size = 3) => ({
    x: x || Math.random() * GAME_SIZE,
    y: y || Math.random() * GAME_SIZE,
    vx: (Math.random() - 0.5) * 2,
    vy: (Math.random() - 0.5) * 2,
    size,
    id: Date.now() + Math.random()
  });

  const startGame = () => {
    setGameActive(true);
    setGameOver(false);
    setShip({ x: 200, y: 250, angle: 0, vx: 0, vy: 0 });
    setBullets([]);
    setScore(0);
    setAsteroids([
      createAsteroid(),
      createAsteroid(),
      createAsteroid(),
      createAsteroid()
    ]);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      if (['w', 'a', 'd', 'arrowup', 'arrowleft', 'arrowright', ' '].includes(key)) {
        e.preventDefault();
        setKeys(k => ({ ...k, [key]: true }));
      }
    };

    const handleKeyUp = (e) => {
      const key = e.key.toLowerCase();
      setKeys(k => ({ ...k, [key]: false }));
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    if (!gameActive || gameOver) return;

    const gameLoop = () => {
      setShip(s => {
        let newShip = { ...s };
        
        if (keys['a'] || keys['arrowleft']) newShip.angle -= 5;
        if (keys['d'] || keys['arrowright']) newShip.angle += 5;
        
        if (keys['w'] || keys['arrowup']) {
          const rad = (newShip.angle * Math.PI) / 180;
          newShip.vx += Math.sin(rad) * 0.3;
          newShip.vy -= Math.cos(rad) * 0.3;
        }

        newShip.vx *= 0.99;
        newShip.vy *= 0.99;
        newShip.x = (newShip.x + newShip.vx + GAME_SIZE) % GAME_SIZE;
        newShip.y = (newShip.y + newShip.vy + GAME_SIZE) % GAME_SIZE;

        return newShip;
      });

      setBullets(b => b
        .map(bullet => ({
          ...bullet,
          x: (bullet.x + bullet.vx + GAME_SIZE) % GAME_SIZE,
          y: (bullet.y + bullet.vy + GAME_SIZE) % GAME_SIZE,
          life: bullet.life - 1
        }))
        .filter(bullet => bullet.life > 0)
      );

      setAsteroids(a => a.map(ast => ({
        ...ast,
        x: (ast.x + ast.vx + GAME_SIZE) % GAME_SIZE,
        y: (ast.y + ast.vy + GAME_SIZE) % GAME_SIZE
      })));

      setAsteroids(currentAsteroids => {
        currentAsteroids.forEach(ast => {
          const dist = Math.hypot(ast.x - ship.x, ast.y - ship.y);
          if (dist < 20 + ast.size * 10) {
            setGameActive(false);
            setGameOver(true);
          }
        });
        return currentAsteroids;
      });

      setBullets(currentBullets => {
        const remainingBullets = [...currentBullets];
        setAsteroids(currentAsteroids => {
          let newAsteroids = [];
          currentAsteroids.forEach(ast => {
            let hit = false;
            remainingBullets.forEach((bullet, i) => {
              const dist = Math.hypot(ast.x - bullet.x, ast.y - bullet.y);
              if (dist < ast.size * 10) {
                hit = true;
                remainingBullets.splice(i, 1);
                setScore(s => s + (4 - ast.size) * 10);
                
                if (ast.size > 1) {
                  newAsteroids.push(createAsteroid(ast.x, ast.y, ast.size - 1));
                  newAsteroids.push(createAsteroid(ast.x, ast.y, ast.size - 1));
                }
              }
            });
            if (!hit) newAsteroids.push(ast);
          });
          return newAsteroids;
        });
        return remainingBullets;
      });

      animationRef.current = requestAnimationFrame(gameLoop);
    };

    animationRef.current = requestAnimationFrame(gameLoop);
    return () => cancelAnimationFrame(animationRef.current);
  }, [gameActive, gameOver, keys, ship]);

  useEffect(() => {
    if (!gameActive || gameOver) return;

    const handleShoot = (e) => {
      const key = e.key.toLowerCase();
      if (key === ' ' && canShoot) {
        e.preventDefault();
        const rad = (ship.angle * Math.PI) / 180;
        setBullets(b => [...b, {
          x: ship.x,
          y: ship.y,
          vx: Math.sin(rad) * 5,
          vy: -Math.cos(rad) * 5,
          life: 60,
          id: Date.now() + Math.random()
        }]);
        setCanShoot(false);
        setTimeout(() => setCanShoot(true), 250);
      }
    };

    window.addEventListener('keydown', handleShoot);
    return () => window.removeEventListener('keydown', handleShoot);
  }, [gameActive, gameOver, ship, canShoot]);

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">☄️ Asteroids</h1>
        <div className="game-instructions">
          W/↑ thrust, A/D or ←/→ rotate, SPACE to shoot
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">Score: {score}</div>
        
        <div className="game-play-area">
          <div className="asteroids-game-area">
            <div 
              className="ship" 
              style={{ 
                left: ship.x, 
                top: ship.y,
                transform: `translate(-50%, -50%) rotate(${ship.angle}deg)`
              }}
            >
              🚀
            </div>
            
            {bullets.map(bullet => (
              <div 
                key={bullet.id} 
                className="asteroid-bullet" 
                style={{ left: bullet.x, top: bullet.y }} 
              />
            ))}
            
            {asteroids.map(ast => (
              <div 
                key={ast.id} 
                className="asteroid" 
                style={{ 
                  left: ast.x, 
                  top: ast.y,
                  width: ast.size * 20,
                  height: ast.size * 20,
                  fontSize: ast.size * 20
                }}
              >
                ☄️
              </div>
            ))}

            {!gameActive && (
              <div className="game-status-overlay">
                {gameOver ? (
                  <div className="status-message lose">💥 Game Over!</div>
                ) : (
                  <div className="status-message start">Press Start!</div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver ? 'Play Again' : 'Start Game'}
          </button>
          <Link to="/" className="btn btn-secondary">Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default Asteroids;
